import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr, money, fdt } from "../../api";
import { Stat } from "../../components/bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { UserPlus, Trash2, Crown } from "lucide-react";

const inputCls = "cr-input !text-xs !py-2 !rounded-xl";
const btnBlack = "bg-[#1C1B1A] text-white text-xs font-medium px-4 py-2 rounded-full hover:bg-[#2C2B2A] transition-all";

export function TeamTab() {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "dispatcher" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/company/team").then((r) => setMembers(r.data.members)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/company/team/invite", form);
      toast.success(`Invite sent to ${form.email}`);
      setForm({ email: "", name: "", role: "dispatcher" });
      setOpen(false);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="team-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#78756E]">{members.length} member{members.length === 1 ? "" : "s"} · team members share jobs, history and billing</p>
        <button data-testid="team-invite-open" onClick={() => setOpen(true)} className={`${btnBlack} flex items-center gap-1.5`}>
          <UserPlus className="w-3.5 h-3.5" /> Invite member
        </button>
      </div>
      <div className="cr-card !p-0">
        {members.map((m) => (
          <div key={m.id} className="flex items-center justify-between px-4 py-3 border-b border-[#F6F3EA] last:border-0" data-testid={`member-${m.id}`}>
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#1C1B1A] text-[#F2D06B] flex items-center justify-center text-xs font-bold">
                {(m.name || m.email)[0].toUpperCase()}
              </span>
              <div>
                <p className="text-xs font-semibold flex items-center gap-1.5">{m.name}
                  {m.is_owner && <Crown className="w-3 h-3 text-[#F2D06B]" />}</p>
                <p className="text-[11px] text-[#A09D95]">{m.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={m.is_owner ? "cr-pill-dark" : "cr-pill-outline"}>{m.is_owner ? "Owner" : m.team_role || "member"}</span>
              {!m.is_owner && (
                <button data-testid={`member-remove-${m.id}`} onClick={async () => {
                  if (!window.confirm(`Remove ${m.email} from the team?`)) return;
                  try { await api.delete(`/company/team/${m.id}`); toast.success("Removed"); load(); }
                  catch (err) { toast.error(fmtErr(err)); }
                }} className="text-[#A09D95] hover:text-red-600 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
          </div>
        ))}
        {members.length === 0 && <p className="text-xs text-[#A09D95] text-center py-8">Loading…</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="team-invite-dialog">
          <DialogHeader><DialogTitle>Invite a team member</DialogTitle></DialogHeader>
          <p className="text-xs text-[#78756E] mt-1">They'll get an email with a one-time link to set their password.</p>
          <form onSubmit={invite} className="space-y-2 mt-3">
            <input data-testid="invite-name" placeholder="Full name" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} w-full`} />
            <input data-testid="invite-email" type="email" placeholder="Email address" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} w-full`} />
            <select data-testid="invite-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={`${inputCls} w-full`}>
              <option value="dispatcher">Dispatcher — can post and manage jobs</option>
              <option value="viewer">Viewer — read-only</option>
            </select>
            <button data-testid="invite-send-btn" disabled={busy} className={`${btnBlack} w-full !py-2.5 disabled:opacity-40`}>
              {busy ? "Sending…" : "Send invite"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function InvoicesTab() {
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  useEffect(() => { api.get("/company/invoices").then((r) => setInvoices(r.data.invoices)).catch(() => {}); }, []);

  const shown = invoices.filter((i) => {
    if (filter !== "all" && i.status !== filter) return false;
    const d = (i.date || "").slice(0, 10);
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="invoices-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex bg-[#EFECE2] p-1 rounded-full gap-1">
          {[["all", "All"], ["paid", "Paid"], ["pending", "Open"], ["refunded", "Refunded"]].map(([v, l]) => (
            <button key={v} data-testid={`inv-filter-${v}`} onClick={() => setFilter(v)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${filter === v ? "bg-[#1C1B1A] text-white" : "text-[#78756E]"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="inv-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} />
          <span className="text-xs text-[#A09D95]">→</span>
          <input data-testid="inv-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} />
        </div>
      </div>
      <div className="cr-card !p-0 cr-table-wrap">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[#A09D95] border-b border-[#E8E4D9]">
            <th className="p-3 font-semibold">Date</th><th className="p-3 font-semibold">Invoice</th>
            <th className="p-3 font-semibold">Type</th><th className="p-3 font-semibold">Amount</th>
            <th className="p-3 font-semibold">Promo</th><th className="p-3 font-semibold">Status</th>
          </tr></thead>
          <tbody>
            {shown.map((i) => (
              <tr key={i.id} className="border-b border-[#F6F3EA] last:border-0" data-testid={`invoice-${i.id}`}>
                <td className="p-3 text-[#78756E]">{fdt(i.date)}</td>
                <td className="p-3 font-medium">{i.label}</td>
                <td className="p-3"><span className="cr-pill-outline">{i.kind === "platform_fee" ? "Fee" : "Plan"}</span></td>
                <td className="p-3 font-semibold">{money(i.amount, i.currency)}</td>
                <td className="p-3 font-mono text-[11px]">{i.promo_code || "—"}</td>
                <td className="p-3">
                  <span className={i.status === "paid" ? "cr-pill-yellow" : i.status === "refunded" ? "cr-pill-dark" : "cr-pill-outline"}>{i.status}</span>
                </td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-[#A09D95]">No invoices match the current filters.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-[#A09D95]">{shown.length} invoice{shown.length === 1 ? "" : "s"}</p>
    </div>
  );
}

export function MetricsTab() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/company/metrics").then((r) => setM(r.data)).catch(() => {}); }, []);
  if (!m) return <p className="text-xs text-[#A09D95] py-10 text-center">Loading…</p>;
  const maxV = Math.max(1, ...m.by_vehicle.map((v) => v.count));

  return (
    <div className="space-y-4" data-testid="metrics-tab">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Stat label="Avg km" value={m.avg_km} sub="per delivery" />
        <Stat label="Median km" value={m.median_km} sub="per delivery" />
        <Stat label="Deliveries" value={m.total_deliveries} sub={`${m.total_jobs} booked`} />
        <Stat label="Top vehicle" value={m.top_vehicle ? m.top_vehicle.replace(/_/g, " ") : "—"} sub="by deliveries" />
        <Stat label="Top booker" value={m.top_booker ? m.top_booker.name.split(" ")[0] : "—"} sub={m.top_booker ? `${m.top_booker.jobs} bookings` : "no bookings"} />
        <Stat label="Active bookers" value={m.active_bookers} sub="team members" />
      </div>
      {Object.keys(m.spend).length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(m.spend).map(([cur, amt]) => (
            <Stat key={cur} label={`Spend ${cur}`} value={money(amt, cur)} sub="delivered jobs" />
          ))}
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-3">
        <section className="cr-card !p-4" data-testid="metrics-usage">
          <h3 className="text-xs font-bold mb-2">Usage over time</h3>
          {m.daily.length === 0 ? <p className="text-[11px] text-[#A09D95]">No bookings in this period.</p> : (
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={m.daily} margin={{ top: 6, right: 6, left: -24, bottom: 0 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#A09D95" }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: "#A09D95" }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 14, border: "1px solid #E8E4D9" }} />
                  <Area type="monotone" dataKey="jobs" stroke="#1C1B1A" strokeWidth={2} fill="#F2D06B" fillOpacity={0.35} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
        <section className="cr-card !p-4" data-testid="metrics-vehicles">
          <h3 className="text-xs font-bold mb-3">Vehicle distribution</h3>
          {m.by_vehicle.length === 0 && <p className="text-[11px] text-[#A09D95]">No deliveries yet.</p>}
          {m.by_vehicle.map((v) => (
            <div key={v.vehicle} className="flex items-center gap-2.5 mb-2">
              <span className="text-[11px] w-24 capitalize text-[#78756E]">{v.vehicle.replace(/_/g, " ")}</span>
              <div className="flex-1 h-2.5 bg-[#F6F3EA] rounded-full overflow-hidden">
                <div className="h-full bg-[#F2D06B] rounded-full" style={{ width: `${(v.count / maxV) * 100}%` }} />
              </div>
              <span className="text-[11px] font-semibold w-6 text-right">{v.count}</span>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
